import React, { useEffect, useRef } from 'react';
import { message as antdMessage } from 'antd';
import { AlertCircleIcon, CheckCircleIcon, CloseIcon } from './Icon';
import { updateMessageLayout } from './MessageLayout';
import './brandMessage.scss';

let activeMessage;

function finishMessage(notice, reason) {
  if (notice.finished) return;
  notice.finished = true;
  clearTimeout(notice.timer);
  notice.onClose?.(reason);
}

function closeMessage(notice = activeMessage, reason = 'cancelled') {
  if (!notice || notice !== activeMessage) return;
  clearTimeout(notice.timer);
  activeMessage = null;
  finishMessage(notice, reason);
  antdMessage.destroy('wiw-system-message');
}

function resumeMessage(notice) {
  if (notice !== activeMessage || notice.persistent) return;
  clearTimeout(notice.timer);
  notice.startedAt = Date.now();
  notice.timer = setTimeout(() => closeMessage(notice, 'expired'), Math.max(0, notice.remaining));
}

function pauseMessage(notice) {
  if (notice !== activeMessage || !notice.timer) return;
  clearTimeout(notice.timer);
  notice.timer = null;
  notice.remaining = Math.max(0, notice.remaining - (Date.now() - notice.startedAt));
}

function MessageBody({ notice, error, content, action }) {
  const body = useRef(null);
  useEffect(() => {
    if (!notice.onShown) return undefined;
    const card = body.current.closest('.ant-message-notice-content') || body.current;
    let timer, observed = false;
    const visible = () => {
      clearTimeout(timer);
      if (!observed || document.hidden || notice.finished || notice.shown) return;
      timer = setTimeout(() => {
        if (notice !== activeMessage || document.hidden || notice.finished || notice.shown) return;
        notice.shown = true;
        notice.onShown();
      }, 1000);
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      observed = entries.some(entry => entry.isIntersecting && entry.intersectionRatio >= .5);
      visible();
    }, { threshold: .5 }) : null;
    if (observer) observer.observe(card);
    else { observed = true; visible(); }
    document.addEventListener('visibilitychange', visible);
    return () => { clearTimeout(timer); observer?.disconnect(); document.removeEventListener('visibilitychange', visible); };
  }, [notice]);
  useEffect(() => {
    const card = body.current.closest('.ant-message-notice-content') || body.current;
    const scroll = action ? body.current.querySelector('.wiw-message-text') : card;
    let start;
    const pause = () => pauseMessage(notice);
    const resume = () => { if (!card.contains(document.activeElement)) resumeMessage(notice); };
    const blur = event => { if (!card.contains(event.relatedTarget)) resume(); };
    const begin = event => {
      pause();
      const touch = event.touches.length === 1 ? event.touches[0] : null;
      // A long notice must remain readable by scrolling. At its bottom the
      // same upward gesture dismisses it, just like a normal short notice.
      start = touch && scroll.scrollHeight - scroll.clientHeight - scroll.scrollTop < 2
        ? { id: touch.identifier, x: touch.clientX, y: touch.clientY } : null;
    };
    const move = event => {
      if (event.touches.length !== 1) { start = null; return; }
      const touch = event.touches[0];
      if (start && start.y - touch.clientY > 8 && Math.abs(touch.clientX - start.x) < start.y - touch.clientY) {
        if (event.cancelable) event.preventDefault();
      }
    };
    const end = event => {
      const touch = Array.from(event.changedTouches).find(value => value.identifier === start?.id);
      const dismiss = event.type !== 'touchcancel' && touch && start.y - touch.clientY >= 40
        && start.y - touch.clientY > Math.abs(touch.clientX - start.x) * 1.5;
      start = null;
      if (dismiss) closeMessage(notice, 'dismissed');
      else resume();
    };
    card.addEventListener('mouseenter', pause);
    card.addEventListener('mouseleave', resume);
    card.addEventListener('focusin', pause);
    card.addEventListener('focusout', blur);
    card.addEventListener('touchstart', begin, { passive: true });
    card.addEventListener('touchmove', move, { passive: false });
    card.addEventListener('touchend', end);
    card.addEventListener('touchcancel', end);
    if (card.contains(document.activeElement)) pause();
    return () => {
      card.removeEventListener('mouseenter', pause); card.removeEventListener('mouseleave', resume);
      card.removeEventListener('focusin', pause); card.removeEventListener('focusout', blur);
      card.removeEventListener('touchstart', begin); card.removeEventListener('touchmove', move);
      card.removeEventListener('touchend', end); card.removeEventListener('touchcancel', end);
    };
  }, [notice, action]);
  const Icon = error ? AlertCircleIcon : CheckCircleIcon;
  return <div ref={body} className="wiw-message-body" role={error ? 'alert' : 'status'} aria-atomic="true">
    {notice.closeLabel && <button type="button" className="wiw-message-close" aria-label={notice.closeLabel}
      onClick={() => closeMessage(notice, 'dismissed')}><CloseIcon /></button>}
    {!action && <Icon />}<span className="wiw-message-text" tabIndex={action ? 0 : undefined}>{content}</span>
    {action && <button type="button" className="wiw-message-action" aria-label={action.label} title={action.label}
      onClick={() => {
        if (notice !== activeMessage) return;
        closeMessage(notice, 'acted');
        action.onClick?.();
      }}>{action.icon || action.label}</button>}
  </div>;
}

// One application-wide holder survives route changes. Reuse its key so repeat
// gestures update this panel instead of stacking large notices over the feed.
const open = (options) => {
  const { content, type = 'info', duration = 4, onClose, action, onShown, engagement = false, closeLabel } = options;
  // Engagement never interrupts an existing operational or guest notice.
  if (engagement && activeMessage) return null;
  const error = type === 'error' || type === 'warning';
  if (activeMessage) finishMessage(activeMessage, 'replaced');
  const notice = { remaining: duration * 1000, persistent: !(duration > 0), timer: null, onClose, onShown, closeLabel };
  activeMessage = notice;
  updateMessageLayout();
  antdMessage.config({ maxCount: 1 });
  // rc-notification retains elapsed time for a reused key. Own the timeout so
  // each new message gets its full reading time, including after three updates.
  const result = antdMessage.open({
    key: 'wiw-system-message',
    duration: 0,
    onClose: () => {
      clearTimeout(notice.timer);
      if (activeMessage === notice) activeMessage = null;
      finishMessage(notice, 'cancelled');
    },
    icon: null,
    className: `wiw-message wiw-message--${error ? 'error' : 'success'}${action ? ' wiw-message--with-action' : ''}${engagement ? ' wiw-message--engagement' : ''}`,
    content: <MessageBody notice={notice} error={error} content={content} action={action} />,
  });
  if (!document.querySelector('.wiw-message-body:hover')) resumeMessage(notice);
  // Keep AntD's callable/thenable result contract, but close only this notice.
  // An unmount or delayed action from its predecessor must not hide a newer one.
  const handle = () => closeMessage(notice);
  handle.close = handle;
  if (result?.then) handle.then = result.then.bind(result);
  if (result?.promise) handle.promise = result.promise;
  return handle;
};

const ofType = type => (content, duration, onClose) => open({
  ...(content && typeof content === 'object' && !React.isValidElement(content) ? content : { content }),
  type,
  ...(duration !== undefined ? { duration } : {}),
  ...(onClose ? { onClose } : {}),
});

const message = {
  open,
  error: ofType('error'),
  warning: ofType('warning'),
  success: ofType('success'),
  info: ofType('info'),
  destroy: () => closeMessage(),
  hasActive: () => Boolean(activeMessage),
};

export default message;
